import React from 'react';

import { SelectChangeEvent, TextField } from '@mui/material';
import { useController, Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSelect } from '../../../../components/form/select';
import { ShuttleFilters, CROSS_BORDER_XPRESS_LOCATION } from '../../../../domain/shuttle';
import { isOneWayTrip, DepartureDate, ReturnDate, TravelType } from '../../../../domain/travels';
import { TravelDateSelect, TravelTypeSelect } from '../../../travels/components';
import { useShuttleLocations } from '../../hooks/use-shuttle-locations';
import dayjs from 'dayjs';

// ********************************************************************************
const CBX_LOCATION_VALUE = CROSS_BORDER_XPRESS_LOCATION.value;
const ORIGIN_FIELD = 'travelPoints.origin';
const DESTINATION_FIELD = 'travelPoints.destination';
// The abstraction of the logic behind the shuttle filters form. The main reason
// for it to exist is to avoid the repetition of the same logic in different forms,
// managing the state of the form and the validation rules in one place.
// Ideally the logic of the form should be in a separate file, but it is here for
// practical reasons.
// TODO: In the future, the logic should be handled by a controller class.
type ShuttleFiltersFormComponents = {
  travelTypeField: JSX.Element;
  passengerField: JSX.Element;
  originField: JSX.Element;
  destinationField: JSX.Element;
  travelDateSelectField: JSX.Element;
};
export const ShuttleFiltersFormComponents = (formManager: UseFormReturn<ShuttleFilters>): ShuttleFiltersFormComponents => {
  const { t } = useTranslation();
  const { shuttleLocations } = useShuttleLocations();

  // == State =====================================================================
  const { control, watch, setValue, clearErrors } = formManager;
  const travelType = watch('travelType');
  const departureDate = watch('departureDate');

  // == Handlers ==================================================================
  // NOTE: Created for readability, the logic could be inlined.
  const travelPoints = {
    [ORIGIN_FIELD]: watch('travelPoints.origin'),
    [DESTINATION_FIELD]: watch('travelPoints.destination'),
  };
  // Handles the change of the origin or destination fields. As well as the logic
  // of the cross border express location. The next set of rules are applied:
  // 1. The user can't select the same location as origin and destination.
  // 2. When one of the locations is different from CROSS BORDER EXPRESS, the other
  //    must be CROSS BORDER EXPRESS.
  const onLocationChanged = (event: SelectChangeEvent, onChange: (value: number | null) => void) => {
    const field = event.target.name;
    const oppositeField = field === ORIGIN_FIELD ? DESTINATION_FIELD : ORIGIN_FIELD;

    const selection = parseInt(event.target.value); /*sanity*/
    const oppositeFieldSelection = travelPoints[oppositeField];

    const isSameLocation = selection === oppositeFieldSelection;
    if (isSameLocation) setValue(oppositeField, null);

    const isNotCBXLocation = selection !== CBX_LOCATION_VALUE;
    if (isNotCBXLocation) setValue(oppositeField, CBX_LOCATION_VALUE);

    onChange(selection);
  };

  // NOTE: This fields are controlled by a component used in different forms and
  //       contexts: TravelDateSelect. An explicit controller is used to apply
  //       validation rules and to update the form state in a consistent way.
  const departureDateField = useController({
    control,
    name: 'departureDate',
    rules: {
      required: true,
      validate: (value) => {
        if (!value) return true; // If no departure date is set, no validation needed

        const isBeforeToday = dayjs(value).isBefore(dayjs(), 'day');
        if (isBeforeToday) {
          // NOTE: Setting error imperatively to reflect the validation instantly 
          //       on the UI, this is necessary because the form validates on submit.
          control.setError('departureDate', {
            message: t('shuttle_service.errors.departure_date_invalid'),
          });
          return false; /* error */
        }

        clearErrors('departureDate');
        return true; /* valid */
      },
    },
  });

  const returnDateField = useController({
    control,
    name: 'returnDate',
    rules: {
      required: !isOneWayTrip(travelType),
      validate: (value) => {
        if (!value) return true; // If no return date is set, no validation needed
        
        const isBeforeDeparture = dayjs(value).isBefore(dayjs(departureDate), 'day');
        if (isBeforeDeparture) { 
          // NOTE: Setting error imperatively to reflect the validation instantly 
          //       on the UI, this is necessary because the form validates on submit.
          control.setError('returnDate', {
            message: t('shuttle_service.errors.return_date_invalid'),
          });
          return false; /* error */
        }

        clearErrors('returnDate');
        return true; /* valid */
      },
    },
  });
  // Handles the change of the departure and return dates.
  const onDateChange = (departureDate: DepartureDate | null, returnDate: ReturnDate | null) => {
    departureDateField.field.onChange(departureDate);
    returnDateField.field.onChange(returnDate);
  };

  const handleTravelTypeChange = (onChange: (type: TravelType) => void) => (type: TravelType) => {
    onChange(type);

    if (isOneWayTrip(type)) {
      returnDateField.field.onChange(null)
    }
  };

  // == Render ====================================================================
  const travelTypeField = (
    <Controller
      control={control}
      name="travelType"
      rules={{ required: true }}
      render={({ field: { value, onChange } }) => (
        <TravelTypeSelect travelType={value} onTravelTypeChange={handleTravelTypeChange(onChange)} />
      )}
    />
  );

  // ..............................................................................
  const originField = (
    <Controller
      control={control}
      name="travelPoints.origin"
      rules={{ required: true }}
      render={({ field: { value, onChange } }) => (
        <FormSelect
          name={ORIGIN_FIELD} /*to identify the field*/
          value={value === null ? '' : `${value}` /*cast*/}
          label={t('inputs.origin')}
          disabled={!shuttleLocations.length}
          onChange={(event: SelectChangeEvent) => onLocationChanged(event, onChange)}
          options={shuttleLocations}
        />
      )}
    />
  );

  const destinationField = (
    <Controller
      control={control}
      name="travelPoints.destination"
      rules={{ required: true }}
      render={({ field: { value, onChange } }) => (
        <FormSelect
          name={DESTINATION_FIELD} /*to identify the field*/
          value={value === null ? '' : `${value}` /*cast*/}
          label={t('inputs.destination')}
          disabled={!shuttleLocations.length}
          onChange={(event: SelectChangeEvent) => onLocationChanged(event, onChange)}
          options={shuttleLocations}
        />
      )}
    />
  );

  // ..............................................................................
  const travelDateSelectField = (
    <TravelDateSelect
      departureDate={departureDateField.field.value}
      returnDate={returnDateField.field.value}
      isReturnDateDisabled={isOneWayTrip(travelType)}
      onDateChange={onDateChange}
      departureDateError={departureDateField?.fieldState?.error}
      returnDateError={returnDateField?.fieldState?.error}
    />
  );

  // ..............................................................................
  const passengerField = (
    <Controller
      control={control}
      rules={{ required: true, min: 1 }}
      name="passengers"
      render={({ field: { value, onChange } }) => (
        <TextField
          sx={{ maxWidth: { md: 160 } }}
          name="passengers"
          type="number"
          label={t('inputs.number_of_passengers')}
          value={value}
          onChange={onChange}
          inputProps={{ min: 1 }}
          fullWidth
        />
      )}
    />
  );

  return {
    travelTypeField,
    originField,
    destinationField,
    travelDateSelectField,
    passengerField,
  };
};
